import type {
  Degree,
  Distance,
  Percentage,
  Point,
  SpatialRectangle,
} from "@pdfking/math"
import type { CompletePage, Page } from "../types/geometry/Page"
import type { PlottingContext } from "../types/options/PlottingContext"
import type { ColorID } from "../types/plot/ID"
import type { PlotMedia } from "../types/plot/PlotMedia"
import type { PlotPath } from "../types/plot/PlotPath"
import type { PlotRect } from "../types/plot/PlotRect"
import type { PlotShape } from "../types/plot/PlotShape"
import type { PlotText } from "../types/plot/PlotText"
import { FullMethodDispatcher } from "@pdfking/tfunc"
import { PlotFunction } from "../types/plot/PlotFunction"

const NO_COLOR = "" as ColorID
const ZERO_RECT = {
  height: 0,
  width: 0,
  x: 0,
  y: 0,
} as SpatialRectangle<Distance>

const createShape = FullMethodDispatcher<
  [Partial<PlotShape> & Pick<PlotShape, "enum">, PlottingContext<unknown>],
  PlotShape
>({
  [PlotFunction.PLOT_MEDIA]: ({ enum: _, ...rest }, _context): PlotMedia => ({
    crop_position: ZERO_RECT,
    media_position: ZERO_RECT,
    page: "",
    print_layer: { enum: "print", order: 0 },
    rotation: 0 as Degree,
    scale: { x: 1, y: 1 } as Point<Percentage>,
    ...rest,
    enum: PlotFunction.PLOT_MEDIA,
  }),
  [PlotFunction.PLOT_PATH]: ({ enum: _, ...rest }, _context): PlotPath => ({
    closed: false,
    fill_color: NO_COLOR,
    points: [],
    rotation: 0 as Degree,
    stroke_color: NO_COLOR,
    ...rest,
    enum: PlotFunction.PLOT_PATH,
  }),
  [PlotFunction.PLOT_RECT]: ({ enum: _, ...rest }, _context): PlotRect => ({
    fill_color: NO_COLOR,
    position: ZERO_RECT,
    rotation: 0 as Degree,
    stroke_color: NO_COLOR,
    ...rest,
    enum: PlotFunction.PLOT_RECT,
  }),
  [PlotFunction.PLOT_TEXT]: ({ enum: _, ...rest }, context): PlotText => ({
    fill_color: NO_COLOR,
    font_name: context.font_family,
    font_size: context.font_size,
    horizontal_align: context.horizontal_align,
    position: ZERO_RECT,
    rotation: 0 as Degree,
    stroke_color: NO_COLOR,
    text: "",
    text_color: "",
    vertical_align: context.vertical_align,
    ...rest,
    enum: PlotFunction.PLOT_TEXT,
  }),
})

export const CreateCompletePage = (
  page: Page,
  context: PlottingContext<unknown>
): CompletePage => {
  const shapes = (page.shapes ?? []).map(shape => createShape(shape, context))
  return {
    bleed: context.page_bleed,
    ...ZERO_RECT,
    ...page,
    shapes,
  }
}
